import { Loading } from "../../app/Status";
import { IconPlus } from "../../app/icons";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  CONTRACT_STATE_LABELS,
  OBLIGATION_BASIS_LABELS,
  OBLIGATION_KIND_LABELS,
  SOURCING_LABELS,
  type ContractSummaryDto,
  type ObligationBasis,
  type ObligationKind,
  type Sourcing,
  type UncoveredObligationDto,
} from "@klantpunt/shared";
import { Tabs } from "../../app/Choice";
import { Meta } from "../../app/Meta";
import { buildingPath } from "../../app/navItems";
import { PageHeader } from "../../app/PageHeader";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useBuildings } from "../../lib/buildingContext";
import { useClientContext } from "../../lib/clientContext";
import { useToast } from "../../lib/toast";
import { BringUnderDialog } from "./BringUnderDialog";
import { ContractDialog } from "./ContractDialog";
import {
  errMsg,
  euro,
  fmtDate,
  intervalText,
  STATE_ORDER,
  stateTone,
  useContracts,
  useContractsRefresh,
  useUncovered,
} from "./useContracts";

type Tab = "contracten" | "zonder";

/** Einde of opzegdatum in één regel: "Opzeggen vóór 30 sep 2026", "Loopt tot 31 dec 2027". */
export function termText(c: ContractSummaryDto) {
  if (c.state === "concept") return "Concept";
  if (c.state === "beeindigd") return `Beëindigd op ${fmtDate(c.currentEndOn)}`;
  if (c.state === "opgezegd") return `Opgezegd, loopt tot ${fmtDate(c.currentEndOn)}`;
  if (c.state === "nog_niet") return `Gaat in op ${fmtDate(c.startsOn)}`;
  if (!c.currentEndOn) return "Onbepaalde tijd";
  if (c.autoRenew && c.noticeBy) return `Opzeggen vóór ${fmtDate(c.noticeBy)}`;
  return `Loopt tot ${fmtDate(c.currentEndOn)}`;
}

function StateChip({ c }: { c: ContractSummaryDto }) {
  return (
    <span className={`chip tone ${stateTone(c)}`.trim()}>
      {c.endsSoon && c.state === "lopend" ? "Loopt af" : CONTRACT_STATE_LABELS[c.state]}
    </span>
  );
}

/**
 * Contracten (docs/CONTRACTEN.md): over alle gebouwen (`/contracten`, klant via de klantkeuze bovenin) of van één
 * gebouw (`/gebouwen/:id/contracten`). Tab *Zonder contract*: uitvoeringen die onder geen lopend contract vallen.
 */
export function ContractsPage() {
  const { id: buildingId } = useParams();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "zonder" && me?.role !== "klant" ? "zonder" : "contracten";
  const { id: clientId } = useClientContext();
  const buildings = useBuildings();
  const building = buildingId ? buildings.data?.find((b) => b.id === buildingId) : undefined;
  const enabled = hasModule(me, "contracts");
  const canEdit = can(me, "contract.manage");
  const list = useContracts(buildingId);
  const [creating, setCreating] = useState(false);
  const [soon, setSoon] = useState(params.get("af") === "1");
  const [q, setQ] = useState("");
  // Zonder contract is een werklijst van de beheerder; de klant ziet alleen de contracten (docs/CONTRACTEN.md, C4).
  const showGaps = me?.role !== "klant";
  const uncovered = useUncovered({ all: true, enabled: enabled && showGaps });

  const setTab = (v: Tab) => {
    const p = new URLSearchParams(params);
    if (v === "zonder") p.set("tab", "zonder");
    else p.delete("tab");
    setParams(p, { replace: true });
  };

  if (!enabled)
    return (
      <div className="center">
        <p>Geen toegang tot de module Contracten.</p>
      </div>
    );

  const ofScope = (bIds: string[], cId: string) =>
    buildingId ? bIds.includes(buildingId) : !clientId || cId === clientId;
  const norm = q.trim().toLocaleLowerCase("nl");
  const contracts = (list.data ?? [])
    .filter((c) =>
      ofScope(
        c.buildings.map((b) => b.id),
        c.client.id,
      ),
    )
    .filter((c) => !soon || c.endsSoon)
    .filter(
      (c) =>
        !norm ||
        [c.name, c.reference, c.supplier?.name, ...c.buildings.map((b) => `${b.code} ${b.name}`)]
          .join(" ")
          .toLocaleLowerCase("nl")
          .includes(norm),
    )
    .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name, "nl"));
  const gaps = (uncovered.data ?? []).filter((u) => ofScope([u.building.id], u.client.id));
  const openGaps = gaps.filter((g) => g.reason === "none" || g.reason === "ended");
  const soonCount = (list.data ?? []).filter(
    (c) =>
      c.endsSoon &&
      ofScope(
        c.buildings.map((b) => b.id),
        c.client.id,
      ),
  ).length;

  return (
    <div className="page dir-page contracts-page">
      <PageHeader
        label={building ? `${building.code} · ${building.name}` : "Alle gebouwen"}
        title="Contracten"
        action={
          canEdit && tab === "contracten" ? (
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              <IconPlus size={20} aria-hidden="true" />
              Contract
            </button>
          ) : undefined
        }
        tabs={
          <Tabs
            label="Contracten"
            value={tab}
            onChange={setTab}
            items={[
              { value: "contracten", label: "Contracten", count: contracts.length },
              ...(showGaps ? [{ value: "zonder" as const, label: "Zonder contract", count: openGaps.length }] : []),
            ]}
          />
        }
      />
      {tab === "contracten" ? (
        <>
          <div className="mjop-filters">
            <label className="field">
              <span className="label">Zoeken</span>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Contract, leverancier of gebouw"
              />
            </label>
            <div className="obcounts" role="group" aria-label="Filter">
              <button type="button" className="kpi" aria-pressed={soon} onClick={() => setSoon(!soon)}>
                Loopt binnen 6 maanden af <b className="num">{soonCount}</b>
              </button>
            </div>
          </div>
          {list.isError && <p className="alert">{errMsg(list.error)}</p>}
          {list.isPending ? (
            <Loading />
          ) : !contracts.length ? (
            <p className="muted">
              {soon || norm
                ? "Geen contracten met deze filters."
                : "Nog geen contracten. Leg een onderhoudscontract vast met + Contract."}
            </p>
          ) : (
            <div className="tscroll">
              <table className="it" aria-label="Contracten">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col" className="dir-hide-narrow">
                      Leverancier
                    </th>
                    {!buildingId && (
                      <th scope="col" className="dir-hide-narrow">
                        Gebouwen
                      </th>
                    )}
                    <th scope="col" className="num dir-hide-narrow">
                      Per jaar
                    </th>
                    <th scope="col">Looptijd</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id}>
                      <td className="tcell-title">
                        <Link to={`/contracten/${c.id}`}>{c.name}</Link>
                        <div className="small muted">
                          <Meta
                            items={[
                              c.reference && `nr. ${c.reference}`,
                              c.lineCount === 1 ? "1 post" : `${c.lineCount} posten`,
                              c.obligationCount === 1 ? "1 uitvoering" : `${c.obligationCount} uitvoeringen`,
                            ]}
                          />
                        </div>
                        <div className="small muted mat-narrow">
                          <Meta items={[c.supplier?.name, c.yearCents != null && `${euro(c.yearCents)} per jaar`]} />
                        </div>
                      </td>
                      <td className="dir-hide-narrow">{c.supplier?.name ?? "–"}</td>
                      {!buildingId && (
                        <td className="dir-hide-narrow">
                          {c.buildings.length === 1 ? (
                            <>
                              <span className="code">{c.buildings[0]!.code}</span> {c.buildings[0]!.name}
                            </>
                          ) : (
                            `${c.buildings.length} gebouwen`
                          )}
                          <div className="muted small">{c.client.name}</div>
                        </td>
                      )}
                      <td className="num dir-hide-narrow">{euro(c.yearCents) || "–"}</td>
                      <td>
                        <StateChip c={c} />
                        <div className="small muted">{termText(c)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <Uncovered rows={gaps} pending={uncovered.isPending} canEdit={canEdit} inBuilding={!!buildingId} />
      )}
      {creating && <ContractDialog buildingId={buildingId} onClose={() => setCreating(false)} />}
    </div>
  );
}

type Show = "open" | "own" | "separate";

/** Tab *Zonder contract*: gaten met filters, selecteren en onder een contract brengen of markeren. */
function Uncovered({
  rows,
  pending,
  canEdit,
  inBuilding,
}: {
  rows: UncoveredObligationDto[];
  pending: boolean;
  canEdit: boolean;
  inBuilding: boolean;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const [show, setShow] = useState<Show>("open");
  const [legal, setLegal] = useState(false);
  const [kind, setKind] = useState("");
  const [gebouw, setGebouw] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bring, setBring] = useState(false);
  const [busy, setBusy] = useState(false);

  const inShow = (r: UncoveredObligationDto) =>
    show === "open" ? r.reason === "none" || r.reason === "ended" : r.reason === show;
  const shown = rows
    .filter(inShow)
    .filter((r) => !legal || r.basis === "wettelijk")
    .filter((r) => !kind || r.kind === kind)
    .filter((r) => !gebouw || r.building.id === gebouw)
    .sort(
      (a, b) =>
        Number(b.basis === "wettelijk") - Number(a.basis === "wettelijk") ||
        a.building.code.localeCompare(b.building.code) ||
        (a.nextDueOn ?? "9").localeCompare(b.nextDueOn ?? "9"),
    );
  const count = (s: Show) =>
    rows.filter((r) => (s === "open" ? r.reason === "none" || r.reason === "ended" : r.reason === s)).length;
  const kinds = [...new Set(rows.filter(inShow).map((r) => r.kind))];
  const gebouwen = [...new Map(rows.map((r) => [r.building.id, r.building])).values()].sort((a, b) =>
    a.code.localeCompare(b.code),
  );
  const ids = shown.map((r) => r.id);
  const pickedIds = ids.filter((id) => picked.has(id));
  const allPicked = ids.length > 0 && pickedIds.length === ids.length;
  const yearTotal = shown.reduce((n, r) => n + (r.yearCostCents ?? 0), 0);
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function mark(sourcing: Sourcing | null) {
    setBusy(true);
    try {
      await api("/contracts/sourcing", { method: "POST", body: { ids: pickedIds, sourcing } });
      toast(
        sourcing
          ? `${pickedIds.length === 1 ? "1 uitvoering" : `${pickedIds.length} uitvoeringen`} op ${SOURCING_LABELS[sourcing]}`
          : "Markering weggehaald",
      );
      setPicked(new Set());
      await refresh();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="muted small">
        Onderhoud en keuringen die onder geen lopend contract vallen. Wat jullie zelf doen of bewust los inkopen,
        markeer je als Eigen uitvoering of Los inkopen.
      </p>
      <div className="mjop-filters">
        {!inBuilding && (
          <label className="field">
            <span className="label">Gebouw</span>
            <select value={gebouw} onChange={(e) => setGebouw(e.target.value)}>
              <option value="">Alle</option>
              {gebouwen.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} · {b.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span className="label">Soort</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Alle</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {OBLIGATION_KIND_LABELS[k as ObligationKind] ?? k}
              </option>
            ))}
          </select>
        </label>
        <div className="obcounts" role="group" aria-label="Filter">
          <button type="button" className="kpi" aria-pressed={legal} onClick={() => setLegal(!legal)}>
            Alleen wettelijk
          </button>
          {(["open", "own", "separate"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className="kpi"
              aria-pressed={show === s}
              onClick={() => {
                setShow(s);
                setPicked(new Set());
              }}
            >
              {s === "open" ? "Zonder contract" : SOURCING_LABELS[s]} <b className="num">{count(s)}</b>
            </button>
          ))}
        </div>
      </div>
      {pending ? (
        <Loading />
      ) : !shown.length ? (
        <p className="muted">
          {show === "open" ? "Alles valt onder een contract of is gemarkeerd." : "Niets in deze selectie."}
        </p>
      ) : (
        <>
          {yearTotal > 0 && (
            <p className="small muted">
              {shown.length === 1 ? "1 uitvoering" : `${shown.length} uitvoeringen`}, samen {euro(yearTotal)} per jaar
              volgens de kosten per keer.
            </p>
          )}
          <div className="tscroll">
            <table className="it" aria-label="Zonder contract">
              <thead>
                <tr>
                  {canEdit && (
                    <th scope="col" className="mat-pick">
                      <input
                        type="checkbox"
                        aria-label="Alles kiezen"
                        checked={allPicked}
                        onChange={() => setPicked(allPicked ? new Set() : new Set(ids))}
                      />
                    </th>
                  )}
                  <th scope="col">Uitvoering</th>
                  <th scope="col" className="dir-hide-narrow">
                    Gebouw
                  </th>
                  <th scope="col" className="dir-hide-narrow">
                    Volgende keer
                  </th>
                  <th scope="col" className="num">
                    Per jaar
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    {canEdit && (
                      <td className="mat-pick">
                        <input
                          type="checkbox"
                          aria-label={`${r.name}${r.asset ? ` bij ${r.asset.code || r.asset.name}` : ""} kiezen`}
                          checked={picked.has(r.id)}
                          onChange={() => toggle(r.id)}
                        />
                      </td>
                    )}
                    <td className="tcell-title">
                      <Link
                        to={
                          r.asset
                            ? `${buildingPath(r.building.id, "assets")}?asset=${r.asset.id}`
                            : buildingPath(r.building.id, "keuringen")
                        }
                      >
                        {r.name}
                      </Link>
                      <div className="small muted">
                        <Meta
                          items={[
                            r.asset ? `${r.asset.code} ${r.asset.name}`.trim() : "Bij het gebouw",
                            OBLIGATION_KIND_LABELS[r.kind as ObligationKind] ?? r.kind,
                            intervalText(r),
                            r.basis === "wettelijk" && OBLIGATION_BASIS_LABELS[r.basis as ObligationBasis],
                            r.reason === "ended" && r.endedContract && `contract ${r.endedContract.name} afgelopen`,
                            r.performer,
                          ]}
                        />
                      </div>
                      <div className="small muted mat-narrow">
                        <Meta items={[`${r.building.code} ${r.building.name}`, fmtDate(r.nextDueOn)]} />
                      </div>
                    </td>
                    <td className="dir-hide-narrow">
                      <span className="code">{r.building.code}</span> {r.building.name}
                      <div className="muted small">{r.client.name}</div>
                    </td>
                    <td className="dir-hide-narrow">{fmtDate(r.nextDueOn) || "–"}</td>
                    <td className="num">{euro(r.yearCostCents) || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {canEdit && pickedIds.length > 0 && (
        <div className="bulkbar" role="region" aria-label="Bulkacties">
          <span className="check">
            <strong className="num">{pickedIds.length}</strong> van {ids.length} gekozen
          </span>
          <span className="acts">
            {show === "open" ? (
              <>
                <button type="button" className="btn small" disabled={busy} onClick={() => setBring(true)}>
                  Onder contract brengen
                </button>
                <button type="button" className="btn small" disabled={busy} onClick={() => void mark("own")}>
                  Eigen uitvoering
                </button>
                <button type="button" className="btn small" disabled={busy} onClick={() => void mark("separate")}>
                  Los inkopen
                </button>
              </>
            ) : (
              <button type="button" className="btn small" disabled={busy} onClick={() => void mark(null)}>
                Markering weghalen
              </button>
            )}
          </span>
        </div>
      )}
      {bring && (
        <BringUnderDialog
          rows={rows.filter((r) => pickedIds.includes(r.id))}
          onClose={() => setBring(false)}
          onDone={() => {
            setBring(false);
            setPicked(new Set());
          }}
        />
      )}
    </>
  );
}
